// src/layout/Header.tsx
import { Layout, Typography, Button, Space } from 'antd';
import { MenuFoldIcon, MenuUnfoldIcon } from '@assets/icons';
import { matchPath, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../store/helpers';
import { useTheme } from '@context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { AppUserMenuDropdown } from '@components/ui/AppUserMenuDropdown/AppUserMenuDropdown';
import styles from './Header.module.css';

type Props = {
  onMenuClick: () => void;
  collapsed?: boolean;
  isMobile?: boolean;
};

const { Header: AntHeader } = Layout;
const { Title } = Typography;

const resolveHeaderTitle = (pathname: string, t: ReturnType<typeof useTranslation>['t']) => {
  const routeTitleMap: Array<{ pattern: string; title: string }> = [
    { pattern: '/organizations/:orgSlug/projects/:projectSlug/channels/:channelId/bundles', title: t('bundles.cardTitle') },
    { pattern: '/projects/:projectSlug/channels/:channelId/bundles', title: t('bundles.cardTitle') },
    { pattern: '/organizations/:orgSlug/projects/:projectSlug/members', title: t('nav.members') },
    { pattern: '/projects/:projectSlug/members', title: t('nav.members') },
    { pattern: '/organizations/:orgSlug/projects/:projectSlug/history', title: t('nav.activity') },
    { pattern: '/projects/:projectSlug/history', title: t('nav.activity') },
    {
      pattern: '/organizations/:orgSlug/projects/:projectSlug/user-properties',
      title: t('nav.userProperties'),
    },
    { pattern: '/projects/:projectSlug/user-properties', title: t('nav.userProperties') },
    { pattern: '/organizations/:orgSlug/projects/:projectSlug/settings', title: t('nav.settings') },
    { pattern: '/projects/:projectSlug/settings', title: t('nav.settings') },
    { pattern: '/organizations/:orgSlug/projects/:projectSlug/dashboard', title: t('nav.dashboard') },
    { pattern: '/projects/:projectSlug/dashboard', title: t('nav.dashboard') },
    { pattern: '/organizations/:orgSlug/projects/:projectSlug', title: t('nav.project') },
    { pattern: '/projects/:projectSlug', title: t('nav.project') },
    { pattern: '/organizations/:orgSlug/projects', title: t('projects.title') },
    { pattern: '/projects', title: t('projects.title') },
    { pattern: '/organizations/:orgSlug/users', title: t('nav.usersAccess') },
    { pattern: '/organizations/:orgSlug/billing', title: t('nav.billing') },
    { pattern: '/organizations/:orgSlug/settings', title: t('nav.settings') },
    { pattern: '/organizations', title: t('nav.organizations') },
    { pattern: '/user/settings', title: t('userMenu.settings') },
    { pattern: '/user/notifications', title: t('userMenu.notifications') },
    { pattern: '/user/tokens', title: t('userMenu.patManagement') },
    { pattern: '/dashboard', title: t('nav.dashboard') },
  ];

  for (const route of routeTitleMap) {
    if (matchPath(route.pattern, pathname)) {
      return route.title;
    }
  }

  return t('nav.dashboard');
};

const Header = ({ onMenuClick, collapsed, isMobile = false }: Props) => {
  const { user } = useAppSelector(state => state.auth);
  const { colors } = useTheme();
  const { t } = useTranslation();
  const location = useLocation();
  const userLabel = `${t('header.loggedInAs')} ${user?.firstName || ''}`.trim();
  const pageTitle = resolveHeaderTitle(location.pathname, t);

  return (
    <AntHeader
      className={styles.header}
      style={{
        background: colors.bg_600,
        padding: isMobile ? '0 12px' : '0 24px',
      }}
    >
      <Space size={isMobile ? 8 : 12}>
        <Button
          type="text"
          icon={collapsed ? <MenuUnfoldIcon /> : <MenuFoldIcon />}
          onClick={onMenuClick}
          styles={{ icon: { color: colors.text_500 } }}
          aria-label={t('header.toggleSidebar')}
          className={styles.menuButton}
        />
        <Title level={4} className={styles.title} style={{ color: colors.text_500 }}>
          {pageTitle}
        </Title>
      </Space>

      <AppUserMenuDropdown
        userLabel={userLabel}
        userName={user?.firstName}
        isMobile={isMobile}
      />
    </AntHeader>
  );
};

export default Header;
